import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField.tsx';
import { ListTypesAPI } from '@openx/types/inventoryList.ts';
import { type UrlTargeting, UrlTargetingFields } from '@openx/types/targeting/domains.ts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { DrawerTopBox } from '../../shared/TargetingDrawer/DrawerTopBox.tsx';

export type DomainsTopBoxProps = {
  inventorylistId: UrlTargeting['inventorylist_id'];
  onInventoryListChange: (newData: UrlTargeting | ((prevState: UrlTargeting) => UrlTargeting)) => void;
  accountUid?: string;
};

export const DomainsTopBox = ({ inventorylistId, onInventoryListChange, accountUid = '' }: DomainsTopBoxProps) => {
  const [showInventoryListDropdown, setShowInventoryListDropdown] = useState(!!inventorylistId);
  const { t } = useTranslation();

  const { useInventoryListsOptionsFetch } = useTargetingContext();
  const { inventoryListsOptions, mapById, loading } = useInventoryListsOptionsFetch(ListTypesAPI.DOMAIN, accountUid);

  return (
    <DrawerTopBox gap={1}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
        }}
      >
        <Grid size={4}>
          <SwitchField
            data-test={'inventory-list-switch'}
            label={t('Select Inventory List')}
            onChange={(_, checked) => {
              setShowInventoryListDropdown(checked);
              onInventoryListChange(prevState => ({
                ...prevState,
                [UrlTargetingFields.inventorylist_id]: checked ? inventorylistId : null,
              }));
            }}
            checked={showInventoryListDropdown}
          />
        </Grid>

        {showInventoryListDropdown && (
          <Grid size={7}>
            <AutoComplete
              sx={{ mt: -1 }}
              textFieldProps={{
                placeholder: t('Select Inventory List'),
                label: t('Inventory List'),
              }}
              data-test={'inventory-list'}
              options={inventoryListsOptions}
              getOptionLabel={option => mapById[option]?.name || ''}
              value={inventorylistId}
              loading={loading}
              onChange={(_, value) =>
                onInventoryListChange(prevState => ({
                  ...prevState,
                  [UrlTargetingFields.inventorylist_id]: value || null,
                }))
              }
              disabled={loading}
            />
          </Grid>
        )}
      </Stack>
    </DrawerTopBox>
  );
};
